/* SEARCH-TAG: page-template  */
import React, { useState, useEffect, useContext } from 'react';
import { graphql } from 'gatsby';
import { shape, arrayOf } from 'prop-types';
import styled from 'styled-components';
import { getImage } from 'gatsby-plugin-image';
import blogArticleProps from 'TMPropTypes/blogArticleProps';
import categoryProps from 'TMPropTypes/categoryProps';
import HeadTags from 'TMComponents/HeadTags';
import Main from 'TMComponents/Layout/Main';
import Input from 'TMComponents/form/Input';
import { Button } from 'TMComponents/Button';
import Canonical from 'TMComponents/Canonical';
import { BreadcrumbsContext } from 'TMComponents/Breadcrumbs';
import { BannerContext } from 'TMComponents/Banner';
import { LayoutContext } from 'TMComponents/Layout';
import BlogCard from 'TMComponents/BlogCard';
import Content from 'TMComponents/Content';
import { spacing } from 'TMDesignSystem/spacing';
import { breakpoints } from 'TMConstants';
import CategoryFilter from './_CategoryFilter';

const PageContainer = styled.div`
    max-width: 1120px;
    margin: 0 auto;
    padding: 0 ${spacing.medium};

    @media (min-width: ${breakpoints.mobileTablet}px) {
        padding: 0 ${spacing.large};
    }
`;

const HeaderContent = styled.div`
    margin: ${spacing.small} 0 ${spacing.large} 0;
`;

const SectionTitle = styled.h2`
    font-size: 24px;
    font-weight: 600;
    color: ${({ theme }) => theme.colorSystem.UIText[1]};
    margin: ${spacing.xxLarge} 0 ${spacing.mediumLarge} 0;
`;

const CategoriesWrapper = styled.div`
    margin: ${spacing.large} 0;
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${spacing.mediumLarge};
    margin: ${spacing.large} 0;

    @media (min-width: ${breakpoints.mobileTablet}px) {
        grid-template-columns: repeat(2, 1fr);
        gap: ${spacing.mediumLarge};
    }

    @media (min-width: ${breakpoints.tabletDesktop}px) {
        grid-template-columns: repeat(3, 1fr);
        gap: ${spacing.mediumLarge};
    }
`;

const LoadMoreWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin: ${spacing.xxLarge} 0;
`;

const BlogLandingPage = ({ data }) => {
    const [filter, setFilter] = useState('');
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [pageNo, setPageNo] = useState(1);

    let articles = data.allArticles.nodes.filter(({ title, summary }) => {
        const lcFilter = filter.toLowerCase();
        return (
            title.toLowerCase().includes(lcFilter) ||
            summary.toLowerCase().includes(lcFilter)
        );
    });

    // If not filtering by text and we've selected a category to filter - filter
    if (!filter && selectedCategories.length > 0) {
        articles = articles.filter(({ categories }) => {
            if (selectedCategories.length === 0) {
                return true;
            }
            return !!categories.find(({ name }) =>
                selectedCategories.includes(name)
            );
        });
    }
    const isBannerAllowed = true;
    const isCrumbsAllowed = true;

    const { setIsNavHidden } = useContext(LayoutContext);
    const { setShowBanner } = useContext(BannerContext);
    const { setCrumbs } = useContext(BreadcrumbsContext);

    useEffect(() => {
        setIsNavHidden(false);
        setShowBanner(isBannerAllowed);
        setCrumbs({
            show: isCrumbsAllowed,
            crumbList: [{ title: 'Blog', link: '/blog/' }]
        });
    }, []);

    return (
        <Main
            isBannerAllowed={isBannerAllowed}
            isCrumbsAllowed={isCrumbsAllowed}
            reducedCrumbSpacing>
            <HeadTags tags={data.pageContent.seoMetaTags.tags} />
            <Canonical href="blog" />
            <PageContainer>
                {data.pageContent.heroContent && (
                    <HeaderContent>
                        <Content
                            content={data.pageContent.heroContent}
                            leftAlign
                            noSidePadding
                        />
                    </HeaderContent>
                )}

                <Input
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder={data.pageContent.searchPlaceholder}
                    search
                />

                <SectionTitle>
                    {filter
                        ? 'Search results'
                        : data.pageContent.latestArticleText}
                </SectionTitle>

                {!filter && (
                    <CategoriesWrapper>
                        <CategoryFilter
                            categories={data.categories.nodes}
                            selected={selectedCategories}
                            onClick={setSelectedCategories}
                        />
                    </CategoriesWrapper>
                )}

                <CardsGrid>
                    {(filter
                        ? articles
                        : articles.slice(0, 6 * pageNo)
                    ).map((article) => (
                        <BlogCard
                            key={article.id}
                            title={article.title}
                            slug={article.slug}
                            image={getImage(article.featuredImage)}
                            excerpt={article.summary}
                            tags={article.categories.map(
                                (cat) => cat.name
                            )}
                            variant="default"
                        />
                    ))}
                </CardsGrid>

                {!filter && articles.length > 6 * pageNo - 1 && (
                    <LoadMoreWrapper>
                        <Button onClick={() => setPageNo(pageNo + 1)}>
                            Load more articles
                        </Button>
                    </LoadMoreWrapper>
                )}
            </PageContainer>
        </Main>
    );
};

/**
 * Fetch page data
 */
export const pageQuery = graphql`
    {
        pageContent: datoCmsBlogLandingPage {
            seoMetaTags {
                ...GatsbyDatoCmsSeoMetaTags
            }
            heroContent {
                ...textStructuredFields
            }
            headerImageDesktop {
                gatsbyImageData(
                    width: 768
                    layout: CONSTRAINED
                    imgixParams: { auto: "compress" }
                )
            }
            headerImageMobile {
                gatsbyImageData(
                    width: 768
                    layout: CONSTRAINED
                    imgixParams: { auto: "compress" }
                )
            }
            latestArticleText
            searchPlaceholder
        }
        categories: allDatoCmsBlogCategory(limit: 100) {
            nodes {
                name
            }
        }

        allArticles: allDatoCmsBlogArticle(
            limit: 18
            sort: { fields: date, order: DESC }
        ) {
            nodes {
                id
                title
                slug
                summary
                featuredImage {
                    gatsbyImageData(
                        width: 400
                        layout: CONSTRAINED
                        placeholder: NONE
                    )
                }
                categories {
                    name
                }
            }
        }
    }
`;

/**
 * Prop types
 */
BlogLandingPage.propTypes = {
    data: shape({
        categories: shape({
            nodes: arrayOf(shape(categoryProps))
        }),
        allArticles: shape({
            nodes: arrayOf(shape(blogArticleProps))
        })
    }).isRequired
};

export default BlogLandingPage;
